import type { EventCategory } from '@shady-pines/db';
import { type User, type Event } from '@shady-pines/db';
import {
  isNonEmptyArray,
  now,
  strongFromEntries,
  strongGroupBy,
  type Uuid,
} from '@shady-pines/types';
import { isEmpty, isNil } from 'lodash';
import { supabaseApi } from '../api/api-store';
import type { EventState } from './types';

export type EventDetailsApi = ReturnType<typeof EventDetailsApi>;

export const EventDetailsApi = (
  get: () => EventState,
  set: (partial: Partial<EventState> | ((state: EventState) => Partial<EventState>)) => void,
) =>
  ({
    updateEventCategories: async (
      id: Uuid<Event>,
      add: Uuid<EventCategory>[],
      remove?: Uuid<EventCategory>[],
    ) => {
      if (isEmpty(add) && isEmpty(remove)) {
        return;
      }
      const originalCategories = get().categoriesByEventId?.[id] ?? [];
      if (isNonEmptyArray(add)) {
        await supabaseApi()
          .from('event_category_mappings')
          .insert(
            add.map((categoryId) => ({
              event_id: id,
              category_id: categoryId,
            })),
          );
      }

      if (isNonEmptyArray(remove)) {
        await supabaseApi()
          .from('event_category_mappings')
          .update({
            deleted_at: now(),
          })
          .in('category_id', remove);
      }

      set((s) => {
        const updatedEventCategories = [
          ...originalCategories.filter((o) => !remove?.some((r) => r === o)),
          ...add,
        ] as const;

        return {
          categoriesByEventId: {
            ...s.categoriesByEventId,
            [id]: updatedEventCategories,
          },
        } as const satisfies Partial<EventState>;
      });
    },
    updateEventFeaturedUsers: async (id: Uuid<Event>, add: Uuid<User>[], remove?: Uuid<User>[]) => {
      if (isEmpty(add) && isEmpty(remove)) {
        return;
      }
      const originalUsers = get().featuredUsersByEventId?.[id] ?? [];
      if (isNonEmptyArray(add)) {
        await supabaseApi()
          .from('event_featured_users')
          .insert(
            add.map((userId) => ({
              event_id: id,
              user_id: userId,
            })),
          );
      }

      if (isNonEmptyArray(remove)) {
        await supabaseApi()
          .from('event_featured_users')
          .update({
            deleted_at: now(),
          })
          .in('user_id', remove);
      }

      set((s) => {
        const updatedFeaturedUsers = [
          ...originalUsers.filter((o) => !remove?.some((r) => r === o)),
          ...add,
        ] as const;

        return {
          featuredUsersByEventId: {
            ...s.featuredUsersByEventId,
            [id]: updatedFeaturedUsers,
          },
        } as const satisfies Partial<EventState>;
      });
    },
    createEventCategory: async (name?: string | null) => {
      const loading = get().loading.createEventCategory;
      if (loading || isNil(name)) {
        return;
      }
      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              createEventCategory: true,
            },
          }) as const satisfies Partial<EventState>,
      );

      const createdCategory = await supabaseApi()
        .from('event_categories')
        .insert({
          name,
        })
        .select('*')
        .single();

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              createEventCategory: false,
            },
            allCategories: isNil(createdCategory.data)
              ? undefined
              : {
                  ...s.allCategories,
                  [createdCategory.data.id]: createdCategory.data,
                },
          }) as const satisfies Partial<EventState>,
      );

      return createdCategory.data?.id;
    },
    fetchEventFeaturedUsers: async () => {
      const loading = get().loading.featuredUsers;

      if (loading) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              featuredUsers: true,
            },
          }) as const satisfies Partial<EventState>,
      );

      const eventFeaturedUsers = await supabaseApi()
        .from('event_featured_users')
        .select('*')
        .is('deleted_at', null);

      const groupedById = strongGroupBy(
        (eventFeaturedUsers.data ?? []).map((fu) => [fu.event_id, fu.user_id]),
        ([eventId]) => eventId,
      );

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              featuredUsers: false,
            },
            featuredUsersByEventId: groupedById,
          }) as const satisfies Partial<EventState>,
      );
    },
    fetchEventCategories: async () => {
      const loading = get().loading.eventCategories;

      if (loading) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              eventCategories: true,
            },
          }) as const satisfies Partial<EventState>,
      );

      const allEventCategories = await supabaseApi()
        .from('event_category_mappings')
        .select('*')
        .is('deleted_at', null);

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              eventCategories: false,
            },
            categoriesByEventId: strongGroupBy(
              (allEventCategories.data ?? []).map((c) => [c.event_id, c.category_id]),
              ([event_id]) => event_id,
            ),
          }) as const satisfies Partial<EventState>,
      );
    },
    fetchAllCategories: async () => {
      const loading = get().loading.allCategories;

      if (loading) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              allCategories: true,
            },
          }) as const satisfies Partial<EventState>,
      );

      const categories = await supabaseApi()
        .from('event_categories')
        .select('*')
        .is('deleted_at', null);

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              allCategories: false,
            },
            allCategories: strongFromEntries((categories.data ?? []).map((c) => [c.id, c])),
          }) as const satisfies Partial<EventState>,
      );
    },
  }) as const;
